import type {
  Background,
  NormalSpreadBackgrounds,
  SavedBackground,
  UpdateBackgroundRequest,
} from '@/api/types/background'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import type { SpreadBackgroundPlacements } from '@/lib/backgrounds/types/spread-background-placements'
import type { SavedSpreadBackgrounds } from './types/saved-spread-backgrounds'

/** Legacy drops always start fully opaque (`new ODBackgroundImage(..., 100)` in page-editor's
 *  `odctrl.js` `dodrop`), and the backend defaults an omitted tint to 100 too. */
export const DEFAULT_BACKGROUND_TINT = 100

const SLOT_FIELDS: ReadonlyArray<[BackgroundDropSlot, keyof NormalSpreadBackgrounds, string]> = [
  ['double', 'doubleBackground', 'DoubleBackground'],
  ['left', 'leftBackground', 'LeftBackground'],
  ['right', 'rightBackground', 'RightBackground'],
]

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function pick(record: Record<string, unknown>, camel: string, pascal: string): unknown {
  return record[camel] ?? record[pascal]
}

function optionalString(value: unknown): string | undefined {
  if (value == null) return undefined
  const s = String(value).trim()

  return s === '' ? undefined : s
}

function readSavedBackground(value: unknown): SavedBackground | null {
  const record = asRecord(value)

  if (!record) return null

  const id = optionalString(pick(record, 'id', 'Id'))

  if (!id) return null

  const tint = Number(pick(record, 'tint', 'Tint'))

  return {
    id,
    imageId: optionalString(pick(record, 'imageId', 'ImageId')),
    colorId: optionalString(pick(record, 'colorId', 'ColorId')),
    tint: Number.isFinite(tint) ? tint : DEFAULT_BACKGROUND_TINT,
    type: optionalString(pick(record, 'type', 'Type')) ?? 'Image',
  }
}

/** Server `NormalSpread` → per-slot map. Tolerates PascalCase keys (the checkpoint metadata
 *  arrives in either casing — see `SpreadCheckpointMetadata`) and null/empty slots. */
export function normalSpreadToSlots(
  normalSpread: NormalSpreadBackgrounds | Record<string, unknown> | null | undefined
): SavedSpreadBackgrounds {
  const record = asRecord(normalSpread)
  const slots: SavedSpreadBackgrounds = {}

  if (!record) return slots

  for (const [slot, camel, pascal] of SLOT_FIELDS) {
    const saved = readSavedBackground(pick(record, camel, pascal))

    if (saved) slots[slot] = saved
  }

  return slots
}

/**
 * Reads `nsDocument.spreadSpecialItems.normalSpread` off a loaded checkpoint's metadata.
 * Returns an empty map (not null) when the spread simply has no backgrounds — only a missing
 * `nsDocument` altogether yields null, meaning "nothing to hydrate from".
 */
export function readSavedSpreadBackgrounds(metadata: unknown): SavedSpreadBackgrounds | null {
  const meta = asRecord(metadata)
  const nsDoc = meta ? asRecord(pick(meta, 'nsDocument', 'NsDocument')) : null

  if (!nsDoc) return null

  const specialItems = asRecord(pick(nsDoc, 'spreadSpecialItems', 'SpreadSpecialItems'))
  const normalSpread = specialItems ? pick(specialItems, 'normalSpread', 'NormalSpread') : null

  return normalSpreadToSlots(asRecord(normalSpread))
}

/** A formula-color background has no image asset, only a `fillColor` — legacy branches the same
 *  way on the drag payload's `colorId` (page-editor `odctrl.js` `dodrop`). */
export function isSolidBackground(background: Background): boolean {
  if (/solid/i.test(background.backgroundType ?? '')) return true

  return !(background.urlScr || background.urlPri) && background.fillColor?.colorId != null
}

/**
 * Builds the PUT body for one slot. Returns null when the background can't be saved — a Solid
 * with no `fillColor.colorId` (the backend requires a numeric catalog ID for Solid).
 */
export function buildBackgroundUpdateRequest(
  background: Background,
  target: BackgroundDropSlot,
  tint: number
): UpdateBackgroundRequest | null {
  const clampedTint = clampBackgroundTint(tint)

  if (isSolidBackground(background)) {
    const colorId = background.fillColor?.colorId

    if (colorId == null) return null

    return {
      target,
      backgroundId: background.backgroundId,
      type: 'Solid',
      colorId: String(colorId),
      tint: clampedTint,
    }
  }

  return { target, backgroundId: background.backgroundId, type: 'Image', tint: clampedTint }
}

export function clampBackgroundTint(tint: number): number {
  return Number.isFinite(tint)
    ? Math.max(0, Math.min(100, Math.round(tint)))
    : DEFAULT_BACKGROUND_TINT
}

/**
 * Re-saves an already-placed slot with a new tint. Built from the saved ref itself rather than
 * the library `Background`, so a tint change never waits on (or depends on) the library lookup.
 * Returns null for a Solid ref missing its `colorId`, which the backend would reject.
 */
export function buildSavedBackgroundTintRequest(
  saved: SavedBackground,
  target: BackgroundDropSlot,
  tint: number
): UpdateBackgroundRequest | null {
  const clampedTint = clampBackgroundTint(tint)

  if (saved.type === 'Solid') {
    if (!saved.colorId) return null

    return {
      target,
      backgroundId: saved.id,
      type: 'Solid',
      colorId: saved.colorId,
      tint: clampedTint,
    }
  }

  return { target, backgroundId: saved.id, type: 'Image', tint: clampedTint }
}

/** Returns `saved` with one slot's tint replaced — unchanged (same reference) when the slot is
 *  empty or already at that tint. */
export function withSavedBackgroundTint(
  saved: SavedSpreadBackgrounds,
  slot: BackgroundDropSlot,
  tint: number
): SavedSpreadBackgrounds {
  const ref = saved[slot]
  const clampedTint = clampBackgroundTint(tint)

  if (!ref || ref.tint === clampedTint) return saved

  return { ...saved, [slot]: { ...ref, tint: clampedTint } }
}

/** The saved-ref shape the backend will echo back for a request — used for the optimistic update
 *  before the PUT resolves. */
export function savedBackgroundFromRequest(request: UpdateBackgroundRequest): SavedBackground {
  return request.type === 'Solid'
    ? { id: request.backgroundId, colorId: request.colorId, tint: request.tint, type: 'Solid' }
    : { id: request.backgroundId, imageId: request.backgroundId, tint: request.tint, type: 'Image' }
}

/**
 * Joins saved refs to their library `Background` (for the image URL / swatch hex). A ref whose
 * background isn't known yet is left out — it renders once the library lookup resolves it.
 */
export function resolveSavedSpreadBackgrounds(
  saved: SavedSpreadBackgrounds,
  backgroundsById: ReadonlyMap<string, Background>
): SpreadBackgroundPlacements {
  const placements: SpreadBackgroundPlacements = {}

  for (const [slot] of SLOT_FIELDS) {
    const ref = saved[slot]
    const background = ref ? backgroundsById.get(ref.id) : undefined

    if (ref && background) placements[slot] = { background, tint: ref.tint }
  }

  return placements
}

/** True when any saved slot references a background not in `backgroundsById` yet. */
export function hasUnresolvedSavedBackgrounds(
  saved: SavedSpreadBackgrounds,
  backgroundsById: ReadonlyMap<string, Background>
): boolean {
  return SLOT_FIELDS.some(([slot]) => {
    const ref = saved[slot]

    return ref != null && !backgroundsById.has(ref.id)
  })
}
